import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../utils/qccaApi";
import { ShieldCheck, Clock } from "lucide-react";

export default function QCCAPublicResults() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/qcca/votes/${id}/public`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="font-headline text-3xl">Results not found.</h1>
          <Link to="/" className="mt-6 inline-flex font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A] hover:text-white">← QCCA landing</Link>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-black text-gray-500 font-mono text-sm p-10">Loading…</div>;

  const { vote, tally, audit_log } = data;
  const yesPct = tally.total_cast ? Math.round((tally.yes / tally.total_cast) * 100) : 0;
  const noPct = tally.total_cast ? Math.round((tally.no / tally.total_cast) * 100) : 0;
  const abstainPct = tally.total_cast ? 100 - yesPct - noPct : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="inline-flex items-center gap-2 border border-[#C5A52A]/40 text-[#C5A52A] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5">
          <ShieldCheck className="w-3 h-3" /> Public results · QCCA
        </div>
        <h1 className="mt-5 font-headline text-4xl sm:text-5xl text-white leading-tight">{vote.title}</h1>
        <div className="mt-2 font-mono text-[11px] text-gray-500 uppercase tracking-[0.22em]">{vote.corporation}</div>
        <p className="mt-5 text-gray-300 leading-relaxed whitespace-pre-wrap">{vote.description}</p>
        <div className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
          <Clock className="w-3 h-3 text-[#C5A52A]" /> Closed {new Date(vote.deadline_iso).toLocaleString()}
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-[#0a0a0a] border border-[#1a1a1a] p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Tally</div>
            <div className="mt-6 space-y-5">
              <Bar label="Yes" value={tally.yes} pct={yesPct} color="#C5A52A" />
              <Bar label="No" value={tally.no} pct={noPct} color="#7a7a7a" />
              <Bar label="Abstain" value={tally.abstain} pct={abstainPct} color="#3a3a3a" />
            </div>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1a1a1a] p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Turnout</div>
            <div className="mt-3 font-headline text-5xl text-white">{tally.turnout_pct}%</div>
            <div className="mt-1 font-mono text-[11px] text-gray-500">{tally.total_cast} of {tally.total_voters}</div>
          </div>
        </div>

        <div className="mt-10 bg-[#050505] border border-[#1a1a1a] p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A52A]">Audit log · anonymous</div>
          <p className="mt-2 text-gray-500 text-xs">Timestamps and choices only — owner identities are not published.</p>
          <div className="mt-4 font-mono text-[11px] text-gray-400 max-h-80 overflow-y-auto">
            {audit_log.length === 0 ? (
              <div className="text-gray-600">No ballots cast yet.</div>
            ) : audit_log.map((row, i) => (
              <div key={`audit-${row.voted_at}-${i}`} className="py-1 grid grid-cols-12 gap-2">
                <span className="col-span-1 text-gray-600">{String(i + 1).padStart(3, "0")}</span>
                <span className="col-span-8">{new Date(row.voted_at).toISOString()}</span>
                <span className={`col-span-3 ${row.choice === "yes" ? "text-[#C5A52A]" : row.choice === "no" ? "text-gray-300" : "text-gray-500"}`}>
                  {row.choice?.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        <Link to="/" className="mt-12 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5A52A] hover:text-white">
          ← QCCA landing
        </Link>
      </div>
    </div>
  );
}

function Bar({ label, value, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between font-mono text-xs mb-1">
        <span className="text-gray-300 uppercase tracking-[0.18em]">{label}</span>
        <span className="text-gray-500">{value} · {pct}%</span>
      </div>
      <div className="h-3 bg-black border border-[#1a1a1a]">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
